// The wcBot's inputs: a few cheap numbers per side, each counted for both teams and differenced (ours
// minus theirs), plus the win conditions' own terms. The model (model.ts) turns them into a win chance.

import { distance } from '../../board';
import { actorTeam, canCarry, cellCounts, moveOf, other, unitById, groundDefense, tagBonus, weightOf, zoneUp, BASE_NEED, DIE } from '../../rules';
import { hasTrait, typeOf, unitCost } from '../../units';
import type { UnitType } from '../../types';
import { hqOf, homeCell } from '../../map';
import type { CtfState, Team, Unit } from '../../types';
import { hid, zoneOffsets, DSTEP as NEIGHBOURS } from '../movement';
import { MAX_ACTIVATIONS } from '../routes';
import { freshThisRound, posOf, routeTurns, strikeArea } from './context';
import type { Ctx, Pos } from './context';

export const WC_FEATURES = [
  'race1', 'race2', 'race3', 'race4', 'race5',   // activations the side needs to score (one-hot; 6+ or none: all 0)
  'toAct',          // the side acts next
  'race1Act',       // ... and can score with that activation
  'tempo',          // activations still to come this round
  'carrying',       // we hold the enemy flag
  'carrierRisk',    // chance our carrier is tagged before it acts again
  'unitRisk',       // the same, summed over our other units
  'riskValue',      // ... each weighted by the unit's points (unitCost / 200), so losing a striker counts more than a squire
  'flagGuard',      // share of the hexes our flag can be picked up from that our standing zones cover
  'escort',         // share of the hexes around our carrier that our other units' standing zones cover
  'advance',        // per unit that can carry: hexes from its teleporter / 10 (at most 1)
  'cells',          // cells held (not home cells)
  'swift',          // Swiftness cells held
  'effects',        // other effect cells held
  'magnet',         // Magnet cells held (pulls the enemy flag toward our HQ each round)
  'jailed',         // our units in prison (prisons option)
  'dom0', 'dom1',   // at the domination mark, one cell short of it (when the rule is on)
  'mercy',          // the cell lead's sign × how near the round limit is, squared (when the rule is on)
] as const;
export type WcFeature = typeof WC_FEATURES[number];
const N = WC_FEATURES.length;
export const IX = Object.fromEntries(WC_FEATURES.map((k, i) => [k, i])) as Record<WcFeature, number>;

const NEXT_ROUND = 0.5;     // a striker that has acted this round only comes next round

// The chance e tags u where it stands, as the engine works it out (tagBreakdown) but leaving out u's
// teammates covering the attacker (where it would strike from isn't known).
function hitChance(s: CtfState, e: Unit, u: Unit, h: number): number {
  let need = BASE_NEED + groundDefense(s, u, u.pos) - tagBonus(s, e.team, u.pos)
    + (hasTrait(s, e, 'clumsy') ? 1 : 0) - (hasTrait(s, e, 'sureStrike') ? 1 : 0) + (hasTrait(s, u, 'tough') ? 1 : 0) - (hasTrait(s, u, 'fragile') ? 1 : 0);
  for (const x of s.units) {
    if (x === e || x.team !== e.team || x.inPrison || !x.pos) continue;
    if (zoneOffsets(s, x, x.facing).includes(h - hid(x.pos))) need -= weightOf(s, x);
  }
  return (DIE + 1 - Math.min(DIE, Math.max(2, need))) / DIE;
}

// Chance u is tagged before it acts again: the best enemy that can strike its hex.
function tagRisk(p: Pos, u: Unit): number {
  const { s } = p;
  const h = hid(u.pos!);
  let best = 0;
  for (let i = 0; i < s.units.length; i++) {
    const e = s.units[i];
    if (e.team === u.team || e.inPrison || !e.pos || !strikeArea(p, i).has(h)) continue;
    best = Math.max(best, hitChance(s, e, u, h) * (freshThisRound(s, e) ? 1 : NEXT_ROUND));
  }
  return best;
}

// Hexes a flag at `fp` can be picked up from (cached per map and spot).
const pickupCache = new Map<string, number[]>();
function pickupHexes(s: CtfState, fp: { q: number; r: number }): number[] {
  const k = `${s.cells.length}|${hid(fp)}|${s.config.flagPickupRange}`;
  let out = pickupCache.get(k);
  if (!out) {
    out = s.cells.flatMap(c => c.hexes).filter(h => distance(h, fp) <= s.config.flagPickupRange).map(hid);
    pickupCache.set(k, out);
  }
  return out;
}

// Activations team t needs to bring the enemy flag home: its carrier's route home, or the best
// would-be carrier's route to the flag (picking up ends that activation) plus the carry home by
// distance at its carrying Move.
export function raceTurns(p: Pos, t: Team): number {
  const { s } = p;
  const hq = hqOf(s.cells, t);
  const ef = s.flags[other(t)];
  if (ef.carrierId) return routeTurns(p, unitById(s, ef.carrierId), [hid(hq)], `hq${t}`);
  const fp = ef.pos!;
  const targets = pickupHexes(s, fp);
  const key = `flag${hid(fp)}`;
  let best = Infinity;
  for (const u of s.units) {
    if (u.team !== t || u.inPrison || !u.pos || !canCarry(s, u)) continue;
    const pick = Math.max(1, routeTurns(p, u, targets, key));
    if (pick > MAX_ACTIVATIONS) continue;
    const carry = Math.max(1, moveOf(s, { ...u, carrying: 'flag' }) - 1);
    best = Math.min(best, pick + Math.max(1, Math.ceil((distance(fp, hq) - 1) / carry)));
  }
  return best;
}

// A unit's worth in the model: its points / 200 (cached per unit type).
const valueCache = new WeakMap<UnitType, number>();
function valueOf(s: CtfState, u: Unit): number {
  const t = typeOf(s, u);
  let v = valueCache.get(t);
  if (v === undefined) { v = unitCost(t) / 200; valueCache.set(t, v); }
  return v;
}
// Hexes covered by team t's zones that stop enemies now (zoneUp), leaving out unit `skip`.
function standingCover(s: CtfState, t: Team, skip?: Unit): Set<number> {
  const out = new Set<number>();
  for (const u of s.units) {
    if (u === skip || u.team !== t || u.inPrison || !u.pos || !zoneUp(s, u)) continue;
    const h = hid(u.pos);
    for (const o of zoneOffsets(s, u, u.facing)) out.add(h + o);
  }
  return out;
}

function side(p: Pos, t: Team, out: Float64Array, sign: number) {
  const { s } = p;
  const race = raceTurns(p, t);
  if (race >= 1 && race <= 5) out[IX.race1 + race - 1] += sign;
  if (actorTeam(s) === t) {
    out[IX.toAct] += sign;
    if (race === 1) out[IX.race1Act] += sign;
  }
  out[IX.tempo] += sign * (s.activations[t].allowed - s.activations[t].used);
  const tele = homeCell(s.cells, t, 'teleporter').centre;
  for (const u of s.units) {
    if (u.team !== t || u.inPrison || !u.pos) continue;
    if (canCarry(s, u)) out[IX.advance] += sign * Math.min(10, distance(u.pos, tele)) / 10;
    const r = tagRisk(p, u);
    if (u.carrying) { out[IX.carrying] += sign; out[IX.carrierRisk] += sign * r; }
    else { out[IX.unitRisk] += sign * r; out[IX.riskValue] += sign * r * valueOf(s, u); }
  }
  const own = s.flags[t];
  if (own.pos) {
    const cover = standingCover(s, t), spots = pickupHexes(s, own.pos);
    out[IX.flagGuard] += sign * spots.filter(h => cover.has(h)).length / spots.length;
  }
  const ef = s.flags[other(t)];
  if (ef.carrierId) {
    const c = unitById(s, ef.carrierId);
    if (c.pos) {
      const cover = standingCover(s, t, c), h = hid(c.pos);
      out[IX.escort] += sign * NEIGHBOURS.filter(d => cover.has(h + d)).length / 6;
    }
  }
  for (const c of s.cells) {
    if (c.home || s.owners[c.id] !== t) continue;
    out[IX.cells] += sign;
    const fx = s.effects[c.id];
    if (fx === 'swift') out[IX.swift] += sign;
    else if (fx === 'magnet') out[IX.magnet] += sign;
    else if (fx) out[IX.effects] += sign;
  }
  out[IX.jailed] += sign * s.units.filter(u => u.team === t && u.inPrison).length;
  // No inputs for army abilities (October 2026): they may be cut, and "still unused this round"
  // inputs taught the bot to hoard them. The search still uses them (Sprint and Rally as branches,
  // a reroll as a gamble), valued by the position they lead to.
  const dom = s.config.dominationCells;
  if (dom > 0) {
    const short = dom - cellCounts(s)[t];
    if (short <= 0) out[IX.dom0] += sign; else if (short === 1) out[IX.dom1] += sign;
  }
}

// The inputs for `team`: its side minus the enemy's.
export function wcFeaturesAt(p: Pos, team: Team): Float64Array {
  const out = new Float64Array(N);
  side(p, team, out, 1);
  side(p, other(team), out, -1);
  const { s } = p;
  if (s.config.mercyRounds > 0) {
    const c = cellCounts(s);
    out[IX.mercy] = Math.sign(c[team] - c[other(team)]) * Math.pow(Math.min(1, s.round / s.config.mercyRounds), 2);
  }
  return out;
}

export function wcFeatures(s: CtfState, team: Team, ctx: Ctx): Float64Array {
  return wcFeaturesAt(posOf(s, ctx), team);
}

// A cheap ranking of where the unit being tried (the mover) could end quietly (end or guard), for
// trimming: only its own part of the model (our race with it there, the enemy carrier's way home if
// there is one, its tag risk and its ground), on the position with it placed (no engine call).
export function quietRank(s: CtfState, ctx: Ctx, team: Team, mover: Unit, w: Float64Array): number {
  const p = posOf(s, ctx);
  let z = 0;
  const race = raceTurns(p, team);
  if (race >= 1 && race <= 5) z += w[IX.race1 + race - 1];
  if (s.flags[team].carrierId) {
    const er = raceTurns(p, other(team));
    if (er >= 1 && er <= 5) z -= w[IX.race1 + er - 1];
  }
  z += w[mover.carrying ? IX.carrierRisk : IX.unitRisk] * tagRisk(p, mover);
  if (canCarry(s, mover)) z += w[IX.advance] * Math.min(10, distance(mover.pos!, homeCell(s.cells, team, 'teleporter').centre)) / 10;
  return z;
}
